
import logger from '../app/logger';
import Platform from '../react/Platform';

/**
 * Returns whether or not the current environment is a mobile device.
 *
 * @returns {boolean}
 */
export function isMobileBrowser() {
    return Platform.OS === 'android' || Platform.OS === 'ios';
}

/** * Returns whether or not the current environment is a mobile device
 * and the screen width is less than 500px.
 * @returns {boolean}
 */
export function isMobileResponsive() {
    const isMobile = getWindowWidth() < 500;

    if (isMobile) {
        return true;
    }

    return false;
}

/**
 * window.innerWdth
 * Returns the window or document object depending on the environment.
 * @returns {Object}
 */
export function getWindowWidth() {
    try {
        const activeWindow
      = 'documentPictureInPicture' in window
      && documentPictureInPicture?.window
      && documentPictureInPicture.window.document?.getElementById?.('react')
          ? documentPictureInPicture.window
          : window;

        return activeWindow.innerWidth;
    } catch (e) {
        logger.error('Error accessing window width:', e);

        return window.innerWidth; // fallback
    }
}

/**
 * window.innerWdth
 * Returns the window or document object depending on the environment.
 * @returns {Object}
 */
export function getWindowHeight() {
    try {
        const activeWindow
      = 'documentPictureInPicture' in window
      && documentPictureInPicture?.window
      && documentPictureInPicture.window.document?.getElementById?.('react')
          ? documentPictureInPicture.window
          : window;

        return activeWindow.innerHeight;
    } catch (e) {
        logger.error('Error accessing window height:', e);

        return window.innerHeight; // fallback
    }
}


/**
 * Returns whether the current screen size is mobile (≤375px).
 * @returns {boolean}
 */
export function isMobileScreen() {
    return getWindowWidth() <= 500;
}

/**
 * Returns whether the current screen size is tablet (376px-900px).
 * @returns {boolean}
 */
export function isTabletScreen() {
    return getWindowWidth() > 500 && getWindowWidth() <= 900;
}

/**
 * Returns whether the current screen size is desktop (901px-1440px).
 * @returns {boolean}
 */
export function isDesktopScreen() {

    return getWindowWidth() > 900 && getWindowWidth() <= 1440;
}

/**
 * Returns whether the current screen size is large desktop (>1440px).
 * @returns {boolean}
 */
export function isLargeDesktopScreen() {
    return getWindowWidth() > 1440;
}

/**
 * Returns the current screen size category.
 * @returns {string} 'mobile' | 'tablet' | 'desktop' | 'large-desktop'
 */
export function getScreenSizeCategory() {
    if (isMobileScreen()) {
        return 'mobile';
    }
    if (isTabletScreen()) {
        return 'tablet';
    }
    if (isDesktopScreen()) {
        return 'desktop';
    }

    return 'large-desktop';
}

/**
 * returns the truncate test with limit
 */
export function truncateText(text, limit) {
    return text?.length > limit ? `${text?.slice(0, limit)}...` : text;
}


/**
 * returns the truncate test with limit
 */
export function truncateTextByWidth(text, containerEl) {
    if (!containerEl || !text) {
        return text;
    }

    const availableWidth = containerEl.offsetWidth;
    const ellipsis = '...';

    // If full text fits, return as is
    if (getTextWidth(text, containerEl) <= availableWidth) {
        return text;
    }

    let start = 0;
    let end = text.length;

    // Binary search (fast + efficient)
    while (start < end) {
        const mid = Math.ceil((start + end) / 2);
        const truncated = text.slice(0, mid) + ellipsis;

        if (getTextWidth(truncated, containerEl) <= availableWidth) {
            start = mid;
        } else {
            end = mid - 1;
        }
    }

    return text.slice(0, start) + ellipsis;
}

/**
   * Get the number of columns for tile view based on participant count and screen size.
   *
   * @private
   * @param {number} participantCount - Number of participants.
   * @returns {number} Number of columns.
   */
function _getTileViewColumns(participantCount) {
    const screenCategory = getScreenSizeCategory();
    const windowWidth = window.innerWidth;

    // Mobile screens (≤375px) - prioritize vertical stacking
    if (windowWidth <= 500 || window.innerWidth <= 500) {
        if (participantCount === 1) {
            return 1;
        }
        if (participantCount === 2) {
            return 1; // Stack vertically for better visibility
        }
        if (participantCount <= 4) {
            return 2;
        }

        return 2; // Maximum 2 columns on mobile
    }

    if (participantCount === 3 && windowWidth <= 1000 && windowWidth >= 500) {
        return 2;
    }

    // Tablet screens (376px-900px) - balanced approach
    if (screenCategory === 'tablet' || (windowWidth > 500 && windowWidth <= 900)) {
        if (participantCount === 1) {
            return 1;
        }
        if (participantCount === 2) {
            return 2;
        }
        if (participantCount === 3) {
            return 3;
        }
        if (participantCount === 4) {
            return 2;
        }
        if (participantCount <= 9 && participantCount > 4) {
            return 3;
        }

        return 4; // Maximum 4 columns on tablet
    }

    // Desktop screens (901px-1440px) - standard layout
    if (screenCategory === 'desktop' || (windowWidth > 900 && windowWidth <= 1440)) {
        switch (participantCount) {
        case 1:
            return 1;
        case 2:
            return 2;
        case 3:
            return 3;
        case 4:
            return 2;
        case 5:
        case 6:
            return 3;
        case 7:
        case 8:
            return 4;
        case 9:
        case 10:
            return 5;
        case 11:
        case 12:
            return 4;
        case 13:
        case 14:
        case 15:
            return 5;
        case 16:
        case 17:
        case 18:
            return 6;
        default:
            return 6; // Maximum 6 columns on desktop
        }
    }

    // Large desktop screens (>1440px) - maximize screen real estate
    switch (participantCount) {
    case 1:
        return 1;
    case 2:
        return 2;
    case 3:
        return 3;
    case 4:
        return 2;
    case 5:
    case 6:
        return 3;
    case 7:
    case 8:
        return 4;
    case 9:
    case 10:
        return 5;
    case 11:
    case 12:
        return 6;
    case 13:
    case 14:
    case 15:
        return 5;
    case 16:
    case 17:
    case 18:
        return 6;
    case 19:
    case 20:
    case 21:
        return 7;
    default:
        // For more than 21 participants on large screens, use maximum of 8 columns
        return 8;
    }
}

/**
 * returns the getDynamicTruncateLimit
 */
export function getDynamicTruncateLimit(participantCount) {
    const containerWidth = window.innerWidth;

    const columns = _getTileViewColumns(participantCount);

    const tileWidth = containerWidth / columns;

    // 1 char ~ 25px width, tweak factor as needed
    let limit = Math.floor(tileWidth / 20);

    // If height is really small, clamp further
    if (tileWidth < 350) {
        limit = Math.max(8, Math.floor(limit * 0.8));
    }

    return Math.max(8, limit); // never below 8 chars
}


/**
 * Returns whether or not the current environment is an ios mobile device.
 *
 * @returns {boolean}
 */
export function isIosMobileBrowser() {
    return Platform.OS === 'ios';
}

/**
 * Checks whether the chrome extensions defined in the config file are installed or not.
 *
 * @param {Object} config - Objects containing info about the configured extensions.
 *
 * @returns {Promise[]}
 */
export function checkChromeExtensionsInstalled(config: Object = {}) {
    const isExtensionInstalled = info =>
        new Promise(resolve => {
            const img = new Image();

            img.src = `chrome-extension://${info.id}/${info.path}`;
            img.onload = function() {
                resolve(true);
            };
            img.onerror = function() {
                resolve(false);
            };
        });
    const extensionInstalledFunction = info => isExtensionInstalled(info);

    return Promise.all(
    (config.chromeExtensionsInfo || []).map(info =>
        extensionInstalledFunction(info)
    )
    );
}

/**
 * formatTimestampToISO
 * @param {*} ISO time
 * @returns
 */
export function formatTimestampToISO(ts) {
    const num = Number(ts);

    if (Number.isNaN(num)) {
        throw new Error('Invalid timestamp: not a number');
    }

    const date = new Date(num);

    if (isNaN(date.getTime())) {
        throw new Error('Invalid time value');
    }

    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * generateCustomerId for givent
 * @returns ID
 */
export function generateCustomerId() {
    const prefix = 'meethourGuest_';
    const chars = '0123456789';
    let randomPart = '';

    // Length similar to example (14 characters)
    for (let i = 0; i < 5; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return prefix + randomPart;
}
